import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  GithubAuthProvider,
  TwitterAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  User as FirebaseUser,
  UserCredential,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  isSignInWithEmailLink,
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { firebaseConfig } from './firebaseConfig';
import authService from '../api/authService';
import { apiRequest } from '../api/axiosConfig';
import tokenManager from '../../utils/tokenManager';

// Firebaseアプリを初期化
const firebaseApp = initializeApp(firebaseConfig);

// Firebase認証インスタンスを取得
const auth = getAuth(firebaseApp);

const persistBackendAuth = (response: any) => {
  if (response?.token) {
    tokenManager.saveToken(response.token);
  }

  if (response?.refresh_token) {
    tokenManager.saveRefreshToken(response.refresh_token);
  } else {
    console.warn('リフレッシュトークンがレスポンスに含まれていません');
  }

  if (response?.user) {
    tokenManager.saveUserData(response.user);
  }
};

const createAuthError = (message: string, code?: string) => {
  const authError = new Error(message) as Error & { code?: string };
  authError.code = code;
  return authError;
};

/**
 * ソーシャルプロバイダを取得
 * @param providerName プロバイダ名（google, github, twitter, facebook, apple）
 * @returns 対応するプロバイダのインスタンス
 * 注意: 現在は使用されていませんが、将来の使用のために保持されています
 */
const getProvider = (providerName: string) => {
  switch (providerName.toLowerCase()) {
    case 'google':
      return new GoogleAuthProvider();
    case 'github':
      return new GithubAuthProvider();
    case 'twitter':
      return new TwitterAuthProvider();
    case 'facebook':
      return new FacebookAuthProvider();
    case 'apple':
      return new OAuthProvider('apple.com');
    default:
      throw new Error(`Unsupported provider: ${providerName}`);
  }
};

/**
 * メールとパスワードでユーザー登録
 * @param email メールアドレス
 * @param password パスワード
 * @param name ユーザー名
 * @returns 認証結果
 */
const registerWithEmailAndPassword = async (email: string, password: string, name: string) => {
  try {
    // Firebaseでユーザー登録
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;
    
    // ユーザープロフィールの表示名を設定
    await updateProfile(firebaseUser, {
      displayName: name
    });
    
    // IDトークンを取得
    const idToken = await firebaseUser.getIdToken();
    
    // バックエンドで認証
    const response = await authenticateWithBackend(firebaseUser, idToken, {
      name,
      email,
      provider: 'password',
      photo_url: null,
    });
    
    return response;
  } catch (error) {
    console.error('Registration failed:', error);
    throw error;
  }
};

/**
 * メールとパスワードでログイン
 * @param email メールアドレス
 * @param password パスワード
 * @returns 認証結果
 */
const loginWithEmailAndPassword = async (email: string, password: string) => {
  try {
    // Firebaseでログイン
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;
    
    // IDトークンを取得
    const idToken = await firebaseUser.getIdToken();
    
    // バックエンドで認証
    const response = await authenticateWithBackend(firebaseUser, idToken, {
      email,
      provider: 'password',
    });
    
    return response;
  } catch (error: any) {
    console.error('Login failed:', error);
    
    // ユーザーフレンドリーなエラーメッセージを提供
    if (error?.code === 'auth/too-many-requests') {
      throw new Error('ログイン試行回数が上限に達しました。しばらく時間をおいてから再度お試しください。');
    } else if (error?.code === 'auth/invalid-credential') {
      throw createAuthError(
        'メールアドレスまたはパスワードが正しくありません。メールリンクで登録した場合は、メールリンクでログインしてください。',
        error.code,
      );
    } else if (error?.code === 'auth/user-not-found') {
      throw new Error('このメールアドレスは登録されていません。');
    } else if (error?.code === 'auth/wrong-password') {
      throw new Error('パスワードが正しくありません。');
    } else if (error?.code === 'auth/invalid-email') {
      throw new Error('メールアドレスの形式が正しくありません。');
    } else if (error?.code === 'auth/user-disabled') {
      throw new Error('このアカウントは無効化されています。');
    }
    
    throw error;
  }
};


  /**
   * Firebase認証情報をバックエンドに送信して認証
   * @param firebaseUser Firebaseユーザー
   * @param idToken Firebase IDトークン
   * @returns バックエンド認証結果
   */
  const authenticateWithBackend = async (
    firebaseUser: FirebaseUser,
    idToken: string,
    overrides: Partial<{
      name: string;
      email: string;
      provider: string;
      provider_user_id: string;
      photo_url: string | null;
    }> = {},
  ) => {
    try {
      // バックエンドへのリクエストデータ
      const authData = {
        firebase_id_token: idToken,
        name: overrides.name ?? firebaseUser.displayName ?? 'User',
        email: overrides.email ?? firebaseUser.email ?? '',
        provider: overrides.provider ?? firebaseUser.providerData[0]?.providerId ?? 'unknown',
        provider_user_id: overrides.provider_user_id ?? firebaseUser.uid,
        photo_url: Object.prototype.hasOwnProperty.call(overrides, 'photo_url')
          ? overrides.photo_url
          : firebaseUser.photoURL
      };
      
      // バックエンドの認証エンドポイントにリクエスト
      const response = await authService.loginWithFirebase(authData);
      
      // tokenManagerを使用してトークンとリフレッシュトークンを保存
      persistBackendAuth(response);
      
      return response;
  } catch (error) {
    console.error('Backend authentication failed:', error);
    throw error;
  }
};

/**
 * ソーシャルログイン処理
 * @param providerName ソーシャルプロバイダ名
 * @returns 認証結果
 * 注意: 現在は使用されていませんが、将来の使用のために保持されています
 */
const signInWithSocial = async (providerName: string) => {
  try {
    const provider = getProvider(providerName);
    
    // プロバイダによっては、追加のスコープやパラメータを設定
    if (provider instanceof GoogleAuthProvider) {
      provider.addScope('profile');
      provider.addScope('email');
    } else if (provider instanceof GithubAuthProvider) {
      provider.addScope('user');
    }
    
    // ポップアップでのログイン
    const result = await signInWithPopup(auth, provider);
    
    // Firebaseユーザー情報を取得
    const firebaseUser = result.user;
    
    // バックエンドに Firebase の認証情報を送信して、アプリ固有のトークンを取得
    const idToken = await firebaseUser.getIdToken();
    
    // バックエンドにリクエストするための関数
    const backendAuthResult = await authenticateWithBackend(firebaseUser, idToken);
    
    return backendAuthResult;
  } catch (error) {
    console.error('Social sign-in failed:', error);
    throw error;
  }
};

/**
 * ログアウト処理
 */
const signOutUser = async () => {
  try {
    // Firebaseからのログアウト
    await signOut(auth);
    
    // アプリからのログアウト
    await authService.logout();
    
    // トークンマネージャーを使用してすべての認証データを削除
    tokenManager.clearAllAuthData();
  } catch (error) {
    throw error;
  }
};

/**
 * 認証状態の変更を監視
 * @param callback コールバック関数
 */
const onAuthStateChange = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, (user) => {
    callback(user);
  });
};

/**
 * Firebaseユーザーのメールアドレスを更新
 * @param newEmail 新しいメールアドレス
 * @param currentPassword 現在のパスワード（再認証用）
 * @returns 更新結果
 */
const updateUserEmail = async (newEmail: string, currentPassword: string) => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('ユーザーがログインしていません');
    }

    // 再認証が必要
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // メールアドレスを更新
    await updateEmail(user, newEmail);

    return { success: true };
  } catch (error) {
    throw error;
  }
};

/**
 * Firebaseユーザーのパスワードを更新
 * @param newPassword 新しいパスワード
 * @param currentPassword 現在のパスワード（再認証用）
 * @returns 更新結果
 */
const updateUserPassword = async (newPassword: string, currentPassword: string) => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('ユーザーがログインしていません');
    }

    // 再認証が必要
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // パスワードを更新
    await updatePassword(user, newPassword);

    return { success: true };
  } catch (error) {
    throw error;
  }
};

/**
 * Firebaseユーザーのプロフィール（表示名）を更新
 * @param newName 新しい表示名
 * @returns 更新結果
 */
const updateUserProfile = async (newName: string) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('ユーザーがログインしていません');
    }

    // プロフィールを更新
    await updateProfile(user, {
      displayName: newName
    });

    return { success: true };
  } catch (error) {
    throw error;
  }
};

/**
 * 管理者用：Firebase UIDでユーザー情報を更新
 * @param firebaseUid Firebase UID
 * @param updates 更新内容
 * @returns 更新結果
 */
const updateUserByUid = async (firebaseUid: string, updates: {
  email?: string;
  password?: string;
  name?: string;
  currentPassword?: string;
}) => {
  try {
    // 注意: この機能は現在のユーザーが対象ユーザーと同じ場合のみ動作します
    // 管理者が他のユーザーを更新するには Firebase Admin SDK が必要です
    const user = auth.currentUser;
    if (!user || user.uid !== firebaseUid) {
      throw new Error('現在のユーザーのみ更新可能です。他のユーザーの更新にはFirebase Admin SDKが必要です。');
    }

    const results: any = {};

    // メールアドレス更新
    if (updates.email && updates.currentPassword) {
      await updateUserEmail(updates.email, updates.currentPassword);
      results.emailUpdated = true;
    }

    // パスワード更新
    if (updates.password && updates.currentPassword) {
      await updateUserPassword(updates.password, updates.currentPassword);
      results.passwordUpdated = true;
    }

    // 表示名更新
    if (updates.name) {
      await updateUserProfile(updates.name);
      results.profileUpdated = true;
    }

    return { success: true, results };
  } catch (error) {
    throw error;
  }
};

/**
 * Magic Link (passwordless sign-in) 用の localStorage キー。
 * Firebase 公式推奨パターン: メール送信時に保存し、コールバック時に読む。
 */
export const MAGIC_LINK_EMAIL_STORAGE_KEY = 'emailForSignIn';
export const MAGIC_LINK_NAME_STORAGE_KEY = 'nameForSignIn';

/**
 * Magic Link（メール認証リンク）を送信する。
 *
 * Tasky 独自の HTML メールでリンクを配送するため、Firebase の
 * `sendSignInLinkToEmail` は呼ばず、バックエンド（Laravel）の
 * `POST /api/auth/magic-link/send` を叩く。
 * バックエンドが Firebase Admin SDK でサインインリンクを生成し、
 * Tasky デザインの HTML メールで送信する。
 *
 * 送信成功後、完了画面で再利用するため localStorage にメールアドレスを保存する。
 */
export const sendMagicLink = async (email: string, name?: string): Promise<void> => {
  await apiRequest.post('auth/magic-link/send', { email });
  window.localStorage.setItem(MAGIC_LINK_EMAIL_STORAGE_KEY, email);
  const trimmedName = name?.trim();
  if (trimmedName) {
    window.localStorage.setItem(MAGIC_LINK_NAME_STORAGE_KEY, trimmedName);
  } else {
    window.localStorage.removeItem(MAGIC_LINK_NAME_STORAGE_KEY);
  }
};

/**
 * 現在の URL が Magic Link のコールバック URL かを判定する。
 */
export const isMagicLinkCallback = (url: string): boolean => {
  return isSignInWithEmailLink(auth, url);
};

/**
 * Magic Link コールバック処理。
 * 1. emailOverride が渡されていればそれを使用、無ければ localStorage から取得
 *    （別デバイス/別タブでリンクを開いた場合は localStorage が空になるため、
 *    フロント側でメアドを再入力させてこの引数に渡す運用を想定）
 * 2. 取得した ID トークンを authService.loginWithFirebase に渡しバックエンド認証
 * 3. 成功時に localStorage のメールアドレスを削除
 */
export const completeMagicLinkSignIn = async (
  url: string,
  emailOverride?: string,
  nameOverride?: string,
) => {
  const email =
    emailOverride || window.localStorage.getItem(MAGIC_LINK_EMAIL_STORAGE_KEY);
  const storedName = window.localStorage.getItem(MAGIC_LINK_NAME_STORAGE_KEY);
  const name = nameOverride?.trim() || storedName?.trim() || undefined;
  if (!email) {
    throw new Error(
      'メールアドレスが見つかりません。お手数ですがもう一度 /signup からやり直してください。',
    );
  }

  const userCredential: UserCredential = await signInWithEmailLink(auth, email, url);
  const firebaseUser = userCredential.user;
  const idToken = await firebaseUser.getIdToken();

  const response = await authService.loginWithFirebase({
    firebase_id_token: idToken,
    name: name || firebaseUser.displayName || email,
    email: firebaseUser.email || email,
    provider: firebaseUser.providerData[0]?.providerId || 'password',
    provider_user_id: firebaseUser.uid,
    photo_url: firebaseUser.photoURL,
  });

  // 認証トークン・ユーザー保存（signInWithSocial と同じ動線）
  if (response?.token) {
    tokenManager.saveToken(response.token);
  }
  if (response?.refresh_token) {
    tokenManager.saveRefreshToken(response.refresh_token);
  }
  if (response?.user) {
    tokenManager.saveUserData(response.user);
  }

  window.localStorage.removeItem(MAGIC_LINK_EMAIL_STORAGE_KEY);
  window.localStorage.removeItem(MAGIC_LINK_NAME_STORAGE_KEY);

  return response;
};

/**
 * Google でサインイン（既存 signInWithSocial の Google 専用ラッパー）。
 * SignupPage / LoginPage から直接参照できるよう named export する。
 */
export const signInWithGoogle = async () => {
  return signInWithSocial('google');
};

const firebaseService = {
  auth,
  signInWithSocial, // 注意: 現在は使用されていませんが、将来の使用のために保持
  signInWithGoogle,
  sendMagicLink,
  completeMagicLinkSignIn,
  isMagicLinkCallback,
  signOutUser,
  onAuthStateChange,
  registerWithEmailAndPassword,
  loginWithEmailAndPassword,
  updateUserEmail,
  updateUserPassword,
  updateUserProfile,
  updateUserByUid,
};

export default firebaseService;
